import type { Dispatch, SetStateAction } from 'react';
import type { Exercise } from '@jackedlogs/shared';

import type { DurableWriteFn } from '../seams';

// The one PATCH that writes a per-exercise timer override (rest + countdown/
// hold default) and reconciles the in-memory catalog. Extracted so the two
// callers — the Settings card (RestDefaultsSettingsCard) and the in-logger
// timer gear (useTimerDefaults) — share ONE copy instead of each hand-rolling
// the same request + catalog patch (§CONSOLIDAMENTO: a second copy that can
// drift is a latent bug). Store only the fields that DIFFER from the global
// default; a value equal to the global is cleared (null) so the row falls
// back to the global instead of carrying a redundant override.
//
// Stock catalog rows live in CATALOG_PK; PATCHing them needs `?tier=stock` so
// the API targets the global row instead of cloning a user-scoped copy under
// USER#<sub>. User-source rows are partition-resolved by the caller's sub, no
// query param. Throws on ApiError — the caller owns the saving/error UI.
export async function saveExerciseTimerDefaults({
  slug,
  restSec,
  holdSec,
  restGlobal,
  holdGlobal,
  catalog,
  setCatalog,
  durableWrite,
  onSaved,
}: {
  slug: string;
  restSec: number;
  holdSec: number;
  restGlobal: number;
  holdGlobal: number;
  catalog: Exercise[] | null;
  setCatalog: Dispatch<SetStateAction<Exercise[] | null>>;
  /** RW-18: mobile passes its outbox-backed durableWrite; web-rnw its
   *  send-only equivalent (packages/ui/src/seams.ts). */
  durableWrite: DurableWriteFn;
  /** Refresh the host's own catalog mirror (mobile: AsyncStorage;
   *  web-rnw: none needed) so the next cold start lands on the value.
   *  Best-effort, optional — omitted callers just skip the warm. */
  onSaved?: () => void;
}): Promise<void> {
  const ex = catalog?.find((x) => x.slug === slug);
  if (!ex) return;
  const restPatch = restSec === restGlobal ? null : restSec;
  const holdPatch = holdSec === holdGlobal ? null : holdSec;
  const tierQuery = ex.source === 'stock' ? '?tier=stock' : '';
  // Queued counts as saved (DX-2/E): the local catalog edit stands either
  // way, and a rejected write throws so the caller's own handler runs.
  const result = await durableWrite({
    method: 'PATCH',
    path: `/api/exercises/${encodeURIComponent(slug)}${tierQuery}`,
    body: { defaultRestSec: restPatch, defaultDurationSec: holdPatch },
  });
  if (result.outcome === 'rejected') throw result.error;
  setCatalog((prev) =>
    prev
      ? prev.map((c) =>
          c.slug === slug
            ? {
                ...c,
                defaultRestSec: restPatch ?? undefined,
                defaultDurationSec: holdPatch ?? undefined,
              }
            : c,
        )
      : null,
  );
  onSaved?.();
}
